import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { doctors } from '../data/mockData';
import { Send, ArrowLeft, Bot, User, Loader2, UserCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const allDoctors = [
  ...doctors.generalCare,
  ...doctors.lifestyle,
  ...doctors.specialists
];

// Generate a unique session ID for this chat session
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const ChatPage = () => {
  const { doctorName } = useParams();
  const decodedName = decodeURIComponent(doctorName);
  const doctor = allDoctors.find(d => d.name === decodedName);
  
  // Check for pending patient info from dashboard
  const [patientInfo, setPatientInfo] = useState(null);
  
  const [sessionId] = useState(() => {
    // Try to get existing session from localStorage or create new one
    const storageKey = `chat_session_${decodedName}`;
    let existingSession = localStorage.getItem(storageKey);
    if (!existingSession) {
      existingSession = generateSessionId();
      localStorage.setItem(storageKey, existingSession);
    }
    return existingSession;
  });
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showPatientBanner, setShowPatientBanner] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for patient info from dashboard search
  useEffect(() => {
    const pendingInfo = sessionStorage.getItem('pendingPatientInfo');
    if (pendingInfo) {
      const info = JSON.parse(pendingInfo);
      setPatientInfo(info);
      setShowPatientBanner(true);
      // Pre-fill the input with patient symptoms
      setInputValue(`Patient: ${info.patientName}\nSymptoms: ${info.symptoms}\nAppointment Type: ${info.appointmentType}`);
      // Clear the pending info
      sessionStorage.removeItem('pendingPatientInfo');
    }
  }, []);

  // Load chat history on mount
  useEffect(() => {
    const loadChatHistory = async () => {
      if (!doctor) return;
      
      try {
        const response = await axios.get(
          `${API}/chat/history/${sessionId}/${encodeURIComponent(decodedName)}`
        );
        
        if (response.data.messages && response.data.messages.length > 0) {
          const formattedMessages = response.data.messages.map((msg, index) => ({
            id: msg.id || index,
            type: msg.role === 'user' ? 'user' : 'bot',
            text: msg.content
          }));
          setMessages(formattedMessages);
        } else {
          // Set initial welcome message if no history
          setMessages([{
            id: 1,
            type: 'bot',
            text: `Hello! I'm ${doctor?.name}, your ${doctor?.specialty}. I'm here to provide healthcare guidance using evidence-based medicine and functional health principles.\n\nI'll first assess if your concern requires emergency attention, then provide recommendations drawing from medical literature (PubMed, MIMS) and functional medicine approaches (James Chestnut's Eat Well, Move Well, Think Well principles).\n\nHow can I help you today?`
          }]);
        }
      } catch (error) {
        console.error('Error loading chat history:', error);
        // Set welcome message on error
        setMessages([{
          id: 1,
          type: 'bot',
          text: `Hello! I'm ${doctor?.name}, your ${doctor?.specialty}. How can I help you today?`
        }]);
      } finally {
        setIsLoading(false);
      }
    };

    loadChatHistory();
  }, [doctor, sessionId, decodedName]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue
    };

    setMessages(prev => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        doctor_name: decodedName,
        message: messageText
      });

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response.data.response
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: "I apologize, but I'm experiencing technical difficulties. Please try again in a moment. If this is a medical emergency, please call 911 immediately."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${sessionId}/${encodeURIComponent(decodedName)}`);
      const newSessionId = generateSessionId();
      localStorage.setItem(`chat_session_${decodedName}`, newSessionId);
      window.location.reload();
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
  };

  if (!doctor) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Doctor not found</h1>
          <Link to="/team">
            <Button className="bg-[#166534] hover:bg-[#14532d] text-white">
              View All Doctors
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 flex flex-col">
        {/* Patient Info Banner - shows when coming from dashboard */}
        {showPatientBanner && patientInfo && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UserCircle className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">{patientInfo.patientName}</p>
                  <p className="text-sm text-blue-700">{patientInfo.appointmentType} • {patientInfo.symptoms}</p>
                </div>
              </div>
              <button
                onClick={() => setShowPatientBanner(false)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Chat Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <img
              src={doctor.image}
              alt={doctor.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-[#166534]"
            />
            <div className="flex-1">
              <h2 className="font-bold text-gray-900">{doctor.name}</h2>
              <p className="text-sm text-gray-500">{doctor.specialty}</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleNewChat}
                className="text-xs"
              >
                New Chat
              </Button>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-gray-500">Online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto py-6">
          <div className="max-w-4xl mx-auto px-4 space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-8 h-8 animate-spin text-[#166534]" />
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start gap-3 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.type === 'user' ? 'bg-[#166534]' : 'bg-gray-200'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="w-5 h-5 text-white" />
                    ) : (
                      <Bot className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    message.type === 'user'
                      ? 'bg-[#166534] text-white rounded-tr-none'
                      : 'bg-white text-gray-800 rounded-tl-none shadow-sm border border-gray-100'
                  }`}>
                    <div className="leading-relaxed whitespace-pre-wrap">{message.text}</div>
                  </div>
                </div>
              ))
            )}
            {isTyping && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-gray-600" />
                </div>
                <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1 items-center">
                    <Loader2 className="w-4 h-4 animate-spin text-[#166534]" />
                    <span className="text-sm text-gray-500 ml-2">Analyzing your inquiry...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Chat Input */}
        <div className="bg-white border-t border-gray-200 px-4 py-4">
          <div className="max-w-4xl mx-auto flex gap-3">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe your symptoms or health concern..."
              className="flex-1 py-6 px-4 rounded-xl border-gray-200 focus:border-[#166534] focus:ring-[#166534]"
              disabled={isTyping}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping}
              className="bg-[#166534] hover:bg-[#14532d] text-white px-6 rounded-xl disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <div className="max-w-4xl mx-auto mt-3 px-2">
            <p className="text-xs text-gray-400 text-center">
              🏥 This AI uses evidence from MIMS, PubMed, and functional medicine principles (James Chestnut's Eat Well, Move Well, Think Well).
              For medical emergencies, please call 911 immediately.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
